import { formatString } from "@format-string/core";
import { Box, Typography, Avatar, Chip, alpha, useTheme } from "@mui/material";
import { Children } from "react";
import {
  Groups as GroupsIcon,
  Person as PersonIcon,
  Badge as BadgeIcon,
  Cake as CakeIcon,
} from "@mui/icons-material";
import { useContextContrato } from "src/Contexts/Lancamentos/Contrato/ContratosContext";
import { FormatDateBR } from "src/shared/Utils/FormatDateBR";
import { ModernCard } from "./shared/ModernCard";

export const CardDependentesModern = () => {
  const { valueContractInternal } = useContextContrato();

  if (!valueContractInternal) return null;

  const { dependentes } = valueContractInternal;
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;

  const hasNoDependentes = !dependentes || dependentes.length === 0;

  return (
    <ModernCard
      title="Dependentes"
      icon={<GroupsIcon />}
      headerAction={
        <Chip
          label={`${dependentes?.length || 0} dependente${dependentes?.length !== 1 ? "s" : ""}`}
          size="small"
          sx={{
            bgcolor: alpha(primaryColor, 0.1),
            color: primaryColor,
            fontWeight: 600,
            fontSize: "12px",
          }}
        />
      }
    >
      {hasNoDependentes ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 4,
            color: "text.secondary",
          }}
        >
          <GroupsIcon sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
          <Typography variant="body2" sx={{ opacity: 0.6 }}>
            Nenhum dependente cadastrado
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {Children.toArray(
            dependentes.map((item, index) => (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: 2,
                  borderRadius: "12px",
                  bgcolor: alpha(primaryColor, 0.04),
                  border: "1px solid",
                  borderColor: alpha(primaryColor, 0.08),
                  transition: "all 0.2s ease",
                  "&:hover": {
                    bgcolor: alpha(primaryColor, 0.08),
                    borderColor: alpha(primaryColor, 0.15),
                  },
                }}
              >
                <Avatar
                  sx={{
                    width: 44,
                    height: 44,
                    bgcolor: alpha(primaryColor, 0.15),
                    color: primaryColor,
                    fontWeight: 600,
                    fontSize: "16px",
                  }}
                >
                  {index + 1}
                </Avatar>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      mb: 0.5,
                    }}
                  >
                    <PersonIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: "text.primary",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.nome}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: { xs: 1, sm: 2 },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <BadgeIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary" }}
                      >
                        {formatString({
                          type: "cpf",
                          value: item.cpf || "",
                        }) as string}
                      </Typography>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <CakeIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary" }}
                      >
                        {FormatDateBR(item.data_nascimento)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            ))
          )}
        </Box>
      )}
    </ModernCard>
  );
};
